import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../parts/inputs/select";
import SvgIcon from "../../../../parts/SvgIcon";
import { addDays, authoredWeeks } from "../../../../utils/flexible-program";
import {
  blockedLandings,
  blockSummary,
  buildWeekName,
  deselectInvalid,
  firstClearStart,
  groupInvalidByBlock,
  importRange,
  planLanding,
  programWeekNumber,
  selectAll,
  templateEditorPath,
  toApiSelection,
  toApiWeekNames,
  validateSelection,
  willReplaceAll,
} from "../../../../utils/import-template-selection";
import ImportBlocks from "./ImportBlocks";
import ImportSidePanel from "./ImportSidePanel";
import ImportTimeline from "./ImportTimeline";

const prettyShort = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D");
const prettyFull = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D, YYYY");

/** Every flexible week of a template: its blocks' weeks, one after another. */
const templateWeekCount = (template) =>
  (template.programs || [])
    .filter((program) => !program.isDeleted)
    .reduce((total, program) => total + (program.numberOfWeeks || 1), 0);

/** The API answers errors as `{ error }` or, from the error middleware, `{ error: { message } }`. */
const errorMessage = (data) => data?.error?.message || data?.error || null;

const DEFAULT_PATTERN = "{block} · Week {week}";

/**
 * Imports (part of) a template into the flexible program. The coach picks a
 * template, which blocks, weeks and workouts to copy, where the import starts
 * and how its weeks are named. The start list reaches back to the program's
 * first week: any week without logged training can be imported over.
 *
 * Empty weeks and workouts block the import until they are fixed in the
 * template or deselected. Anything the API still refuses is explained inline.
 * Importing at the first week of a multi-week program replaces every week and
 * asks for an explicit confirmation first.
 */
const ImportTemplateModal = ({ cycle, athlete, templates, api, onClose }) => {
  const { t } = useTranslation("coaching-portal");
  const today = Number(moment().format("YYYYMMDD"));

  const importable = (templates || []).filter(
    (template) =>
      template.type === "coach" &&
      !["flexible", "teams"].includes(template.storeType) &&
      templateWeekCount(template) > 0
  );

  const existingWeeks = authoredWeeks(cycle);
  const authoredStarts = existingWeeks.map((program) =>
    Number(program.startDate)
  );

  const [templateId, setTemplateId] = useState("");
  const [preview, setPreview] = useState({ status: "idle" });
  const [selection, setSelection] = useState({});
  const [startDate, setStartDate] = useState(null);
  const [naming, setNaming] = useState({
    mode: "default",
    pattern: DEFAULT_PATTERN,
  });
  const [step, setStep] = useState("pick"); // "pick" | "confirm"
  const [submitting, setSubmitting] = useState(false);
  const [serverResult, setServerResult] = useState(null);

  const template = importable.find((item) => item._id === templateId);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // The selected template's blocks > weeks > workouts, and the start weeks.
  useEffect(() => {
    if (!templateId) {
      setPreview({ status: "idle" });
      return undefined;
    }
    let cancelled = false;
    setPreview({ status: "loading" });
    setServerResult(null);
    setStep("pick");

    (async () => {
      try {
        const res = await api("flexible-import-template", {
          query: { cycle: cycle._id, template: templateId, today },
        });
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (!data?.success) {
          setPreview({ status: "error", message: errorMessage(data) });
          return;
        }
        setPreview({ status: "ready", ...data });
        setSelection(selectAll(data.blocks));
        const open = data.targetWeeks.filter((week) => !week.loggedWork);
        const suggested = open.find(
          (week) => week.startDate === data.suggestedStart
        );
        setStartDate(
          (suggested || open.find((week) => week.startDate > today) || open[0])
            ?.startDate ?? null
        );
      } catch (error) {
        console.error("flexible-import-template preview failed", error);
        if (!cancelled) {
          setPreview({ status: "error", message: error?.message });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [templateId, cycle._id, api, today]);

  const ready = preview.status === "ready";
  const blocks = preview.blocks || [];
  const invalid = useMemo(
    () => validateSelection(blocks, selection),
    [blocks, selection]
  );
  const invalidByBlock = useMemo(() => groupInvalidByBlock(invalid), [invalid]);
  const landings = useMemo(
    () =>
      ready && startDate
        ? planLanding(blocks, selection, startDate, preview.anchor)
        : [],
    [ready, blocks, selection, startDate, preview.anchor]
  );
  const weekCount = landings.length;
  const workoutCount = landings.reduce((total, l) => total + l.workouts, 0);
  const blockCount = new Set(landings.map((l) => l.blockOrder)).size;

  const landingDates = landings.map((landing) => landing.startDate);
  const conflicts = existingWeeks.filter((program) =>
    landingDates.includes(Number(program.startDate))
  );
  const replaceAll = startDate
    ? willReplaceAll(authoredStarts, startDate)
    : false;
  const range = startDate ? importRange(startDate, weekCount) : null;
  const emptyTemplate =
    ready && blocks.every((block) => blockSummary(block).workouts === 0);

  const namingExample = landings.length
    ? buildWeekName({
        naming,
        templateName: preview.templateName,
        blockName: blocks.find((b) => b.order === landings[0].blockOrder)?.name,
        blockOrder: landings[0].blockOrder,
        week: landings[0].week,
        programWeek: landings[0].programWeek,
        startDate: landings[0].startDate,
        order: landings[0].programWeek,
      })
    : "";

  const runImport = async () => {
    setSubmitting(true);
    setServerResult(null);
    const apiSelection = toApiSelection(blocks, selection);
    const weekNames = toApiWeekNames(naming);
    try {
      const res = await api("flexible-import-template", {
        method: "POST",
        body: {
          cycle: cycle._id,
          template: templateId,
          startDate,
          today,
          confirm: true,
          ...(replaceAll ? { replaceAll: true } : {}),
          ...(apiSelection ? { selection: apiSelection } : {}),
          ...(weekNames ? { weekNames } : {}),
        },
      });
      const data = await res.json().catch(() => null);
      if (data?.success) {
        // The page's weeks and preview are loaded server-side, so reload it.
        window.location.assign(
          `/athlete-portal/programs/flexible-program/${cycle._id}${
            athlete ? `?athlete=${athlete}` : ""
          }`
        );
        return;
      }
      setServerResult({
        error: errorMessage(data) || t("flexible.importTemplate.failed"),
        blocked: data?.blocked,
      });
      setStep("pick");
    } catch (error) {
      console.error("flexible-import-template failed", error);
      setServerResult({
        error: error?.message || t("flexible.importTemplate.failed"),
      });
      setStep("pick");
    } finally {
      setSubmitting(false);
    }
  };

  const needsConfirm = replaceAll || conflicts.length > 0;
  // Logged training can't be replaced: any landing on such a week blocks the
  // import. The POST stays the source of truth (it also guards the repeat of
  // the last week), and its reason is shown if it still rejects.
  const targetWeeks = preview.targetWeeks || [];
  const blockedRange = blockedLandings(landings, targetWeeks);
  const clearStart = blockedRange.length
    ? firstClearStart(targetWeeks, weekCount, startDate)
    : null;
  const blockedImport =
    invalid.length > 0 || !weekCount || !startDate || blockedRange.length > 0;
  const useSuggested = () => {
    setServerResult(null);
    setStartDate(preview.suggestedStart);
  };

  const templateSelect = (
    <div>
      <span className="imp-label">
        {t("flexible.importTemplate.templateLabel")}
      </span>
      <Select
        name="template"
        displayLabel={
          template
            ? undefined
            : t("flexible.importTemplate.templatePlaceholder")
        }
        options={importable.map((item) => ({
          value: item._id,
          text: `${item.name} (${t("flexible.importTemplate.weeks", { count: templateWeekCount(item) })})`,
        }))}
        value={templateId}
        onChange={setTemplateId}
        overlay={true}
      />
    </div>
  );

  const skeleton = (
    <div className="imp-blocks" aria-live="polite">
      {[0, 1].map((index) => (
        <div key={index} className="imp-block on">
          <div className="imp-skel" style={{ width: "40%" }} />
          <div className="imp-skel" style={{ width: "65%" }} />
        </div>
      ))}
    </div>
  );

  let content;
  if (step === "confirm") {
    content = (
      <div className="imp-single">
        <div className="imp-notice" role="alert">
          <img src="/img/new-icons/chip-warning.svg" alt="" />
          <div className="imp-notice-main">
            <div className="imp-notice-title">
              {t("flexible.importTemplate.overwriteBadge")}
            </div>
            <p className="imp-notice-text">
              {replaceAll
                ? t("flexible.importTemplate.replaceAllMessage", {
                    count: existingWeeks.length,
                    imported: weekCount,
                  })
                : t("flexible.importTemplate.overwriteMessage", {
                    count: conflicts.length,
                    weeks: conflicts.map((program) => program.name).join(", "),
                  })}
            </p>
          </div>
        </div>
      </div>
    );
  } else if (!importable.length) {
    content = (
      <div className="imp-single">
        <p>{t("flexible.importTemplate.noTemplates")}</p>
      </div>
    );
  } else {
    content = (
      <div className="imp-body">
        <div className="imp-main">
          {templateSelect}
          {preview.status === "loading" && skeleton}
          {preview.status === "error" && (
            <div className="imp-notice danger" role="alert">
              <div className="imp-notice-main">
                <div className="imp-notice-title">
                  {preview.message || t("flexible.importTemplate.loadFailed")}
                </div>
              </div>
            </div>
          )}
          {emptyTemplate && (
            <div className="imp-empty">
              <h4>{t("flexible.importTemplate.emptyTemplate.title")}</h4>
              <p>
                {t("flexible.importTemplate.emptyTemplate.text", {
                  name: preview.templateName,
                  weeks: t("flexible.importTemplate.blockCount", {
                    count: blocks.reduce((n, b) => n + b.weeks.length, 0),
                  }),
                })}
              </p>
              <a className="imp-linkbtn" href={templateEditorPath(templateId)}>
                <SvgIcon
                  src="/img/new-icons/export.svg"
                  useCurrentColor
                  classNames="imp-linkicon"
                />
                {t("flexible.importTemplate.emptyTemplate.open", {
                  name: preview.templateName,
                })}
              </a>
            </div>
          )}
          {ready && !emptyTemplate && (
            <ImportBlocks
              blocks={blocks}
              selection={selection}
              onChange={(next) => {
                setServerResult(null);
                setSelection(next);
              }}
              landings={landings}
              invalidByBlock={invalidByBlock}
              onDeselect={(items) => {
                setServerResult(null);
                setSelection((current) => deselectInvalid(current, items));
              }}
              templateId={templateId}
              templateName={preview.templateName}
              editorPath={templateEditorPath}
            />
          )}
        </div>

        <aside className="imp-side">
          {preview.status === "loading" && (
            <div className="imp-timeline">
              {[0, 1, 2].map((index) => (
                <div key={index} className="imp-tl-row">
                  <div className="imp-skel" style={{ width: "2rem" }} />
                  <div className="imp-tl-mid">
                    <div className="imp-skel" style={{ width: "60%" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {ready && !emptyTemplate && startDate && (
            <>
              <ImportSidePanel
                programName={cycle.name}
                preview={preview}
                today={today}
                startDate={startDate}
                onStartDate={(date) => {
                  setServerResult(null);
                  setStartDate(date);
                }}
                naming={naming}
                onNaming={setNaming}
                namingExample={namingExample}
                replaceAll={replaceAll}
                conflicts={conflicts}
                existingCount={existingWeeks.length}
                onUseSuggested={useSuggested}
              />
              <ImportTimeline
                landings={landings}
                blocks={blocks}
                templateName={preview.templateName}
                naming={naming}
                existingWeeks={existingWeeks}
                startDate={startDate}
                anchor={preview.anchor}
                loggedStarts={targetWeeks
                  .filter((week) => week.loggedWork)
                  .map((week) => week.startDate)}
              />
              {blockedRange.length > 0 && (
                <div className="imp-notice danger" role="alert">
                  <img src="/img/new-icons/chip-warning.svg" alt="" />
                  <div className="imp-notice-main">
                    <div className="imp-notice-title">
                      {t("flexible.importTemplate.blockedRangeTitle")}
                    </div>
                    <p className="imp-notice-text">
                      {t("flexible.importTemplate.blockedRangeText", {
                        weeks: blockedRange
                          .map((landing) =>
                            t("flexible.importTemplate.blockedRangeWeek", {
                              week: landing.programWeek,
                              dates: `${prettyShort(landing.startDate)} – ${prettyShort(addDays(landing.startDate, 6))}`,
                            })
                          )
                          .join(", "),
                      })}
                    </p>
                    {clearStart && (
                      <div className="imp-note-actions">
                        <button
                          type="button"
                          data-button-type="secondary"
                          className="sm"
                          onClick={() => {
                            setServerResult(null);
                            setStartDate(clearStart);
                          }}
                        >
                          {t("flexible.importTemplate.useSuggested", {
                            week: programWeekNumber(preview.anchor, clearStart),
                          })}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {serverResult?.blocked?.length > 0 && (
                <div className="imp-notice danger" role="alert">
                  <div className="imp-notice-main">
                    <div className="imp-notice-title">{serverResult.error}</div>
                    <p className="imp-notice-text">
                      {serverResult.blocked
                        .map((week) =>
                          t("flexible.importTemplate.blockedWeek", {
                            start: prettyShort(week.startDate),
                            name: week.name || "",
                          })
                        )
                        .join(" ")}
                    </p>
                  </div>
                </div>
              )}
              {serverResult?.error && !serverResult.blocked?.length && (
                <div className="imp-notice danger" role="alert">
                  <div className="imp-notice-main">
                    <div className="imp-notice-title">{serverResult.error}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    );
  }

  let summary = "";
  if (step === "pick" && ready && !emptyTemplate) {
    if (invalid.length) {
      summary = t("flexible.importTemplate.fixToContinue", {
        count: invalid.length,
      });
    } else if (!weekCount) {
      summary = t("flexible.importTemplate.nothingSelected");
    }
  }

  return (
    <div
      className="wk-modal-overlay import-template-overlay open"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="wk-modal import-template-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-template-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="wk-modal-header">
          <div>
            <h2 className="alt" id="import-template-title">
              {t("flexible.importTemplate.title")}
            </h2>
            <p className="imp-sub">{t("flexible.importTemplate.subtitle")}</p>
          </div>
          <button
            type="button"
            className="wk-modal-close"
            data-button-type="tertiary"
            data-icon-only
            onClick={onClose}
            aria-label={t("flexible.manager.cancel")}
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>

        {content}

        <footer className="wk-modal-footer">
          <p className="imp-sum" aria-live="polite">
            {summary ||
              (step === "pick" && ready && !emptyTemplate && weekCount > 0 && (
                <>
                  <b>
                    {t("flexible.importTemplate.nBlocks", {
                      count: blockCount,
                    })}
                  </b>
                  {" · "}
                  <b>
                    {t("flexible.importTemplate.workoutsCount", {
                      count: workoutCount,
                    })}
                  </b>{" "}
                  {t("flexible.importTemplate.across", {
                    weeks: t("flexible.importTemplate.blockCount", {
                      count: weekCount,
                    }),
                  })}
                  {" · "}
                  {prettyShort(range.start)} – {prettyFull(range.end)}
                </>
              ))}
          </p>
          <button
            type="button"
            data-button-type="secondary"
            onClick={step === "confirm" ? () => setStep("pick") : onClose}
            disabled={submitting}
          >
            {t("flexible.manager.cancel")}
          </button>
          <button
            type="button"
            data-button-type="primary"
            disabled={
              submitting || (step === "pick" && (!ready || blockedImport))
            }
            onClick={
              step === "pick" && needsConfirm
                ? () => setStep("confirm")
                : runImport
            }
          >
            {step === "confirm"
              ? replaceAll
                ? t("flexible.importTemplate.confirmReplaceAll")
                : t("flexible.importTemplate.confirm")
              : weekCount > 0 && !emptyTemplate
                ? t("flexible.importTemplate.importWeeks", { count: weekCount })
                : t("flexible.importTemplate.next")}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ImportTemplateModal;
